/**
 * BonsaiMailAssist
 *
 * Inline AI assistant panel for drafting or reviewing outbound client emails
 * within the portal. Accepts an optional initial prompt / context string and
 * returns a structured draft (subject + body) that the fee-earner can edit
 * and copy directly into Bonsai Mail.
 *
 * Usage:
 *   <BonsaiMailAssist
 *     submissionId={id}
 *     recipientName={submission.client_name}
 *     context="Follow up on ID verification"
 *   />
 */
import { useState, useCallback, useRef } from "react";
import { API } from "../config/api";
import axios from "axios";
import {
  Mail, Sparkles, Loader2, RefreshCw, Copy, Check,
  ChevronDown, ChevronUp, AlertTriangle, Info, Pencil, X
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

import { errorMessage } from "../utils/errorMessage";
// ---------------------------------------------------------------------------
// Copyable block — subject or body
// ---------------------------------------------------------------------------
function CopyBlock({ label, value, mono = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-200">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1 rounded text-slate-400 hover:text-slate-600 transition-colors"
          title={`Copy ${label}`}
        >
          {copied
            ? <Check className="w-3.5 h-3.5 text-status-green" />
            : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <p className={`px-3 py-2.5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed ${
        mono ? "font-mono text-xs" : ""
      }`}>
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom prompt composer
// ---------------------------------------------------------------------------
function PromptComposer({ onSubmit, loading }) {
  const [open, setOpen]     = useState(false);
  const [text, setText]     = useState("");
  const textareaRef         = useRef(null);

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleSubmit = () => {
    if (!text.trim()) return;
    onSubmit(text.trim());
    setOpen(false);
    setText("");
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
        data-testid="mail-assist-custom-prompt-toggle"
      >
        <Pencil className="w-3 h-3" /> Custom prompt
      </button>
    );
  }

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="e.g. Draft a chaser for the outstanding ID documents…"
        rows={3}
        className="w-full px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 resize-none focus:outline-none"
        data-testid="mail-assist-prompt-input"
      />
      <div className="flex items-center justify-end gap-2 px-3 py-2 bg-slate-50 border-t border-slate-200">
        <button
          type="button"
          onClick={() => { setOpen(false); setText(""); }}
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Cancel
        </button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!text.trim() || loading}
          className="bg-teal hover:bg-teal-hover text-white h-7 text-xs gap-1"
        >
          {loading
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Sparkles className="w-3 h-3" />}
          Generate
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function BonsaiMailAssist({
  submissionId,
  recipientName = "",
  context       = "",
  className     = "",
}) {
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [expanded, setExpanded]   = useState(true);
  const [error, setError]         = useState(null);
  const [activeCtx, setActiveCtx] = useState(context);

  const fetchDraft = useCallback(async (customContext) => {
    const ctx = customContext ?? activeCtx;
    setLoading(true);
    setError(null);
    if (customContext) setActiveCtx(customContext);
    try {
      const res = await axios.post(`${API}/ai/mail-assist/${submissionId}`, {
        recipient_name: recipientName,
        context: ctx,
      });
      setResult(res.data);
    } catch (err) {
      const msg = errorMessage(err, "Mail Assist could not generate a draft.");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [submissionId, recipientName, activeCtx]);

  // ── idle ─────────────────────────────────────────────────────────────────
  if (!result && !error) {
    return (
      <div
        className={`border border-slate-200 rounded-md p-4 bg-slate-50 ${className}`}
        data-testid="bonsai-mail-assist-idle"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-700">Bonsai Mail Assist</span>
            </div>
            <p className="text-xs text-slate-500">
              Draft a client email based on this matter — edit and send via Bonsai Mail.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => fetchDraft()}
            disabled={loading}
            className="shrink-0 bg-teal hover:bg-teal-hover text-white gap-1.5"
            data-testid="mail-assist-trigger-btn"
          >
            {loading
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting...</>
              : <><Sparkles className="w-3.5 h-3.5" /> Draft Email</>}
          </Button>
        </div>

        {/* Custom prompt available even before first result */}
        <div className="mt-3 pt-3 border-t border-slate-200">
          <PromptComposer onSubmit={fetchDraft} loading={loading} />
        </div>
      </div>
    );
  }

  // ── error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={`border border-red-200 rounded-md p-4 bg-status-red-dim ${className}`} data-testid="bonsai-mail-assist-error">
        <div className="flex items-center gap-2 text-status-red mb-2">
          <AlertTriangle className="w-4 h-4" />
          <span className="font-medium text-sm">Draft failed</span>
        </div>
        <p className="text-xs text-status-red mb-3">{error}</p>
        <Button variant="outline" size="sm" onClick={() => fetchDraft()} className="gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Retry
        </Button>
      </div>
    );
  }

  // ── result panel ─────────────────────────────────────────────────────────
  return (
    <div
      className={`border border-slate-200 rounded-md overflow-hidden bg-white ${className}`}
      data-testid="bonsai-mail-assist-result"
    >
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-200"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-slate-700 text-sm">Bonsai Mail Assist</span>
          {result.tone && (
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full border border-slate-200">
              {result.tone}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); fetchDraft(); }}
            disabled={loading}
            className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
            title="Re-draft"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-3">

          {/* Notes to fee-earner before the draft */}
          {result.notes && (
            <div className="flex items-start gap-2 p-2.5 rounded-md bg-status-amber-dim border border-amber-200">
              <Info className="w-3.5 h-3.5 mt-0.5 text-status-amber shrink-0" />
              <p className="text-xs text-status-amber">{result.notes}</p>
            </div>
          )}

          {/* Subject */}
          {result.subject && (
            <CopyBlock label="Subject" value={result.subject} />
          )}

          {/* Body */}
          {result.body && (
            <CopyBlock label="Body" value={result.body} />
          )}

          {/* Custom prompt */}
          <div className="pt-1">
            <PromptComposer onSubmit={fetchDraft} loading={loading} />
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 flex items-center gap-1">
            <Info className="w-3 h-3 shrink-0" />
            AI draft only — review carefully before sending. Never send without reading.
          </p>
        </div>
      )}
    </div>
  );
}

export default BonsaiMailAssist;
